import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Globe, KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import PageLayout from "../components/PageLayout";
import { StreamModeToggle } from "../components/StreamModeToggle";
import { Playlists } from "../lib/api";
import { useCatalog } from "../contexts/CatalogContext";
import { useAuth } from "../contexts/AuthContext";
import { getTimezone, listTimezones, setTimezone } from "../lib/prefs";
import { setStreamMode, useStreamMode } from "../lib/streamMode";
import type { Playlist } from "../lib/types";

export default function SettingsPage() {
  const { user } = useAuth();
  const { playlists, refreshPlaylists, refreshCatalog } = useCatalog();
  const [adding, setAdding] = useState(false);
  const [editing, setEditing] = useState<Playlist | null>(null);
  const [tz, setTz] = useState<string>(getTimezone());
  // The effective zone must always be one of the options. A <select> whose
  // value matches no <option> silently renders the first one, so the screen
  // would report a timezone the app is not using — see prefs.ts. Canonical
  // lists also omit aliases some systems still report (Asia/Calcutta,
  // Europe/Kiev), so guard the general case rather than just UTC.
  const tzList = useMemo(() => {
    const zones = listTimezones();
    return zones.includes(tz) ? zones : [tz, ...zones];
  }, [tz]);
  const streamMode = useStreamMode();

  return (
    <PageLayout>
      <div className="px-4 md:px-8 py-4 max-w-3xl">
        <h1 className="font-display text-[36px] sm:text-[44px] tracking-[-0.5px] text-text-primary mb-6">
          Settings
        </h1>

        <Section title="Display">
          <div className="bg-surface rounded-xl p-4 border border-muted/30">
            <label className="flex flex-col items-start gap-3 sm:flex-row sm:items-center">
              <Globe size={16} className="text-accent-gold flex-shrink-0 hidden sm:block" />
              <div className="flex-1 min-w-0">
                <p className="text-[13px] font-ui font-medium text-text-primary">
                  Time zone
                </p>
                <p className="text-[11px] font-mono uppercase tracking-[0.04em] text-text-secondary">
                  Used everywhere EPG times are shown
                </p>
              </div>
              <select
                value={tz}
                onChange={(e) => {
                  setTz(e.target.value);
                  setTimezone(e.target.value);
                }}
                className="h-10 w-full bg-canvas border border-muted/40 rounded-lg px-3 text-[13px] font-ui text-text-primary gold-focus transition-fast sm:w-auto sm:min-w-[200px]"
              >
                {tzList.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
            </label>
          </div>
        </Section>

        <Section title="Streaming">
          <StreamModeToggle
            value={streamMode}
            onChange={setStreamMode}
            title="Stream mode"
            helperText={
              streamMode === "direct"
                ? "Direct uses the panel URL when possible. HTTPS pages will fall back to the proxy for HTTP-only streams (browser mixed-content rule)."
                : "Every stream is routed through this server. Useful for diagnosing whether a problem lives in the panel or the proxy."
            }
          />
        </Section>

        <Section title="Account">
          <div className="bg-surface rounded-xl p-4 border border-muted/30 flex flex-wrap items-center gap-3">
            <div className="flex-1 min-w-0">
              <p className="text-[14px] font-ui font-medium text-text-primary">{user?.username}</p>
              <p className="text-[11px] font-mono uppercase tracking-[0.04em] text-text-secondary">
                {user?.is_admin ? "Administrator" : "Viewer"}
              </p>
            </div>
            <Link
              to="/change-password"
              className="px-4 py-2 rounded-pill border border-muted/40 text-[12px] font-ui font-medium uppercase tracking-[0.04em] text-text-primary hover:border-accent-gold hover:text-accent-gold transition-fast inline-flex items-center gap-2"
            >
              <KeyRound size={14} /> Change password
            </Link>
            {user?.is_admin && (
              <Link
                to="/admin"
                className="px-4 py-2 rounded-pill bg-accent-gold text-canvas text-[12px] font-ui font-medium uppercase tracking-[0.04em] hover:bg-accent-gold-light transition-fast"
              >
                Admin panel
              </Link>
            )}
          </div>
        </Section>

        <Section
          title="Playlists"
          right={
            <button
              onClick={() => setAdding(true)}
              className="px-3 py-1.5 rounded-pill bg-accent-gold text-canvas text-[12px] font-ui font-medium uppercase tracking-[0.04em] hover:bg-accent-gold-light transition-fast inline-flex items-center gap-1.5"
            >
              <Plus size={14} /> Add
            </button>
          }
        >
          {playlists.length === 0 && (
            <p className="text-[13px] font-ui text-text-secondary mb-3">
              You don't have any playlists yet — add one to start watching.
            </p>
          )}
          <div className="space-y-2">
            {playlists.map((p) => (
              <div
                key={p.id}
                className="bg-surface rounded-xl p-4 border border-muted/30 flex items-center gap-3"
              >
                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-ui font-medium text-text-primary truncate">{p.name}</p>
                  <p className="text-[11px] font-mono uppercase tracking-[0.04em] text-text-secondary truncate">
                    {p.kind === "xtream"
                      ? `xtream · ${p.xtream_scheme}://${p.xtream_host}:${p.xtream_port}`
                      : `m3u · ${p.m3u_url_masked || ""}`}
                  </p>
                </div>
                <button
                  onClick={() => setEditing(p)}
                  className="p-2 text-text-secondary hover:text-accent-gold transition-fast"
                  aria-label="Edit"
                  title="Edit"
                >
                  <Pencil size={16} />
                </button>
                <button
                  onClick={async () => {
                    if (!confirm(`Delete playlist "${p.name}"?`)) return;
                    await Playlists.delete(p.id);
                    await refreshPlaylists();
                  }}
                  className="p-2 text-text-secondary hover:text-danger transition-fast"
                  aria-label="Delete"
                  title="Delete"
                >
                  <Trash2 size={16} />
                </button>
              </div>
            ))}
          </div>
        </Section>

        {adding && (
          <AddPlaylistModal
            onClose={() => setAdding(false)}
            onCreated={async () => {
              setAdding(false);
              await refreshPlaylists();
              await refreshCatalog(true);
            }}
          />
        )}
        {editing && (
          <EditPlaylistModal
            playlist={editing}
            onClose={() => setEditing(null)}
            onSaved={async () => {
              setEditing(null);
              await refreshPlaylists();
              await refreshCatalog(true);
            }}
          />
        )}
      </div>
    </PageLayout>
  );
}

function EditPlaylistModal({
  playlist,
  onClose,
  onSaved,
}: {
  playlist: Playlist;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [name, setName] = useState(playlist.name);
  const [xtUrl, setXtUrl] = useState(
    playlist.kind === "xtream"
      ? `${playlist.xtream_scheme}://${playlist.xtream_host}:${playlist.xtream_port}`
      : ""
  );
  const [xtUser, setXtUser] = useState("");
  const [xtPass, setXtPass] = useState("");
  const [m3u, setM3u] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      const body: Parameters<typeof Playlists.update>[1] = { name };
      if (playlist.kind === "xtream") {
        if (xtUrl) body.xtream_url = xtUrl.trim();
        if (xtUser) body.xtream_username = xtUser.trim();
        if (xtPass) body.xtream_password = xtPass;
      } else {
        if (m3u) body.m3u_url = m3u.trim();
      }
      await Playlists.update(playlist.id, body);
      onSaved();
    } catch (e: unknown) {
      setErr(
        (e as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
          "Failed to save"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-canvas/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[480px] glass-panel rounded-xl p-6"
      >
        <h2 className="font-display text-[28px] tracking-[-0.5px] text-text-primary mb-1">
          Edit playlist
        </h2>
        <p className="text-[12px] font-ui text-text-secondary mb-5">
          Leave credential fields blank to keep the existing values.
        </p>
        <form onSubmit={submit} className="space-y-3">
          <Field label="Display name" value={name} onChange={setName} autoFocus />
          {playlist.kind === "xtream" ? (
            <>
              <Field label="Server URL" value={xtUrl} onChange={setXtUrl} placeholder="http://host:port" />
              <Field label="Username" value={xtUser} onChange={setXtUser} placeholder="(unchanged)" />
              <Field label="Password" type="password" value={xtPass} onChange={setXtPass} placeholder="(unchanged)" />
            </>
          ) : (
            <Field
              label="M3U URL"
              value={m3u}
              onChange={setM3u}
              placeholder="(unchanged — paste a new URL to replace)"
            />
          )}
          {err && <p className="text-[12px] font-ui text-danger">{err}</p>}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 rounded-xl border border-muted/40 text-[13px] font-ui font-medium uppercase tracking-[0.04em] text-text-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 h-11 rounded-xl bg-accent-gold text-canvas text-[13px] font-ui font-medium uppercase tracking-[0.04em] hover:bg-accent-gold-light transition-fast disabled:opacity-50"
            >
              {busy ? "Saving…" : "Save"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Section({ title, right, children }: { title: string; right?: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <div className="flex items-center justify-between mb-3">
        <h2 className="font-data font-semibold text-[11px] uppercase tracking-[0.13em] text-text-secondary">
          {title}
        </h2>
        {right}
      </div>
      {children}
    </div>
  );
}

function AddPlaylistModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [kind, setKind] = useState<"xtream" | "m3u">("xtream");
  const [name, setName] = useState("");
  const [xtUrl, setXtUrl] = useState("");
  const [xtUser, setXtUser] = useState("");
  const [xtPass, setXtPass] = useState("");
  const [m3u, setM3u] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true);
    setErr(null);
    try {
      if (kind === "xtream") {
        await Playlists.create({
          name: name || `Xtream — ${new URL(xtUrl.includes("://") ? xtUrl : `http://${xtUrl}`).host}`,
          kind: "xtream",
          xtream_url: xtUrl.trim(),
          xtream_username: xtUser.trim(),
          xtream_password: xtPass,
        });
      } else {
        await Playlists.create({
          name: name || "M3U Playlist",
          kind: "m3u",
          m3u_url: m3u.trim(),
        });
      }
      onCreated();
    } catch (e: unknown) {
      setErr(
        (e as { response?: { data?: { detail?: string } } }).response?.data?.detail ||
          "Failed to add playlist"
      );
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-canvas/70 backdrop-blur-sm flex items-end sm:items-center justify-center p-4" onClick={onClose}>
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-[480px] glass-panel rounded-xl p-6"
      >
        <h2 className="font-display text-[28px] tracking-[-0.5px] text-text-primary mb-1">Add playlist</h2>
        <p className="text-[12px] font-ui text-text-secondary mb-5">
          Stored encrypted, only you can use it.
        </p>

        <div className="grid grid-cols-2 gap-2 p-1 bg-surface rounded-lg mb-5">
          {(["xtream", "m3u"] as const).map((k) => (
            <button
              key={k}
              type="button"
              onClick={() => setKind(k)}
              className={`py-2 rounded-md text-[12px] font-ui font-medium uppercase tracking-[0.04em] transition-fast ${
                kind === k ? "bg-canvas text-accent-gold" : "text-text-secondary hover:text-text-primary"
              }`}
            >
              {k === "xtream" ? "Xtream API" : "M3U URL"}
            </button>
          ))}
        </div>

        <form onSubmit={submit} className="space-y-3">
          <Field label="Display name" value={name} onChange={setName} placeholder="My provider" />
          {kind === "xtream" ? (
            <>
              <Field label="Server URL" value={xtUrl} onChange={setXtUrl} placeholder="http://host:port" autoFocus />
              <Field label="Username" value={xtUser} onChange={setXtUser} />
              <Field label="Password" type="password" value={xtPass} onChange={setXtPass} />
            </>
          ) : (
            <Field
              label="M3U URL"
              value={m3u}
              onChange={setM3u}
              placeholder="http://host/get.php?username=...&password=..."
              autoFocus
            />
          )}

          {err && <p className="text-[12px] font-ui text-danger">{err}</p>}
          <div className="flex gap-2 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 h-11 rounded-xl border border-muted/40 text-[13px] font-ui font-medium uppercase tracking-[0.04em] text-text-secondary hover:border-text-secondary transition-fast"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={busy}
              className="flex-1 h-11 rounded-xl bg-accent-gold text-canvas text-[13px] font-ui font-medium uppercase tracking-[0.04em] hover:bg-accent-gold-light transition-fast disabled:opacity-50"
            >
              {busy ? "Adding…" : "Add"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function Field({
  label,
  value,
  onChange,
  type = "text",
  placeholder,
  autoFocus,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  placeholder?: string;
  autoFocus?: boolean;
}) {
  return (
    <div>
      <label className="block text-[11px] font-ui font-medium uppercase tracking-[0.06em] text-text-secondary mb-1">
        {label}
      </label>
      <input
        type={type}
        autoFocus={autoFocus}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="w-full h-10 bg-surface border border-muted/40 rounded-lg px-3 text-[14px] font-ui text-text-primary gold-focus transition-fast"
      />
    </div>
  );
}
